import React from 'react';
import { styled } from '@linaria/react';

// SVGLineElement, by default, accepts strings for x1, y1, x2, y2.
// We need it to be numbers, so we can do the math.
type SvgWithoutCoords = Omit<
  React.SVGProps<SVGLineElement>,
  'x1' | 'y1' | 'x2' | 'y2'
>;

interface Props extends SvgWithoutCoords {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  isActive?: boolean;
  isHidden?: boolean;
  endCapLength?: number;
}

function SizeLine({
  x1,
  y1,
  x2,
  y2,
  isActive = false,
  isHidden = false,
  endCapLength = 10,
  mask,
  ref,
  ...delegated
}: Props) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const angle = Math.atan2(dy, dx);

  const perpendicularAngle = angle + Math.PI / 2;

  const capRadius = endCapLength / 2;

  // First perpendicular line (at x1,y1)
  const start1X = x1 + Math.cos(perpendicularAngle) * capRadius;
  const start1Y = y1 + Math.sin(perpendicularAngle) * capRadius;
  const end1X = x1 - Math.cos(perpendicularAngle) * capRadius;
  const end1Y = y1 - Math.sin(perpendicularAngle) * capRadius;

  // Second perpendicular line (at x2,y2)
  const start2X = x2 + Math.cos(perpendicularAngle) * capRadius;
  const start2Y = y2 + Math.sin(perpendicularAngle) * capRadius;
  const end2X = x2 - Math.cos(perpendicularAngle) * capRadius;
  const end2Y = y2 - Math.sin(perpendicularAngle) * capRadius;

  return (
    <Wrapper
      data-is-active={isActive}
      data-is-hidden={isHidden}
      mask={mask}
    >
      <BackgroundLine
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        {...delegated}
      />
      <BackgroundEndCap
        x1={start1X}
        y1={start1Y}
        x2={end1X}
        y2={end1Y}
        {...delegated}
      />
      <BackgroundEndCap
        x1={start2X}
        y1={start2Y}
        x2={end2X}
        y2={end2Y}
        {...delegated}
      />
      <ForegroundLine
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        {...delegated}
      />
      <ForegroundEndCap
        x1={start1X}
        y1={start1Y}
        x2={end1X}
        y2={end1Y}
        {...delegated}
      />
      <ForegroundEndCap
        x1={start2X}
        y1={start2Y}
        x2={end2X}
        y2={end2Y}
        {...delegated}
      />
    </Wrapper>
  );
}

const Wrapper = styled.g`
  transition: opacity 200ms;

  &[data-is-hidden='true'] {
    opacity: 0;
  }
`;

const BaseLine = styled.line`
  stroke-dasharray: 4 6;
  stroke-linecap: round;
  transition:
    stroke-dasharray 500ms,
    stroke-dashoffset 500ms;

  ${Wrapper}[data-is-active='true'] & {
    stroke-dasharray: 10 0;
    stroke-dashoffset: 3;
    transition:
      stroke-dasharray 200ms,
      stroke-dashoffset 200ms;
  }
`;

const BackgroundLine = styled(BaseLine)`
  stroke: var(--color-background);
  stroke-width: 5px;
`;

const ForegroundLine = styled(BaseLine)`
  stroke: white;
  stroke-width: 2px;

  ${Wrapper}[data-is-active='true'] & {
    stroke: var(--wham-primary-500);
  }
`;

const BackgroundEndCap = styled.line`
  stroke: var(--color-background);
  stroke-width: 5px;
`;

const ForegroundEndCap = styled.line`
  stroke: white;
  stroke-width: 2px;

  ${Wrapper}[data-is-active='true'] & {
    stroke: var(--wham-primary-500);
  }
`;

export default SizeLine;
